import { React } from '../../../Imports';
import { BulkRatingRequest, VideoEventResponse, VideoEventScoreResponse } from '$Generated/api';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { FormControl, FormControlLabel, Grid, RadioGroup } from '@mui/material';
import { progressLoaderEnum, loaderDetailInfo } from '$Components/Shared/MaterialUIComponents/StyledProgressLoader';
import { StyledRadio } from './VideoEventListStyles';
import { useState, useEffect } from 'react';
import * as scssStyles from '$CSS/settings.scss';
import { isEncompass } from '$Utilities/integrationPlatform';

type VideoEventListBulkScoreModalProps = {
    isOpen: boolean;
    handleModalClosed: (isSingleScore?: boolean) => void;
    selectedEventsCount: number;
    videoEventRatings: VideoEventScoreResponse[];
    handleSubmit: (body: BulkRatingRequest) => Promise<boolean>;
    getSelectedEvents: () => Promise<number[]>;
    refreshVideoEventsList?: () => void;
    isSingleScore?: boolean;
    selectedEvent: VideoEventResponse | undefined;
    skipSubmitMessage?: boolean;
};

type Styles = {
    clearScoreSection: string;
    clearScoreText: string;
    clearScoreTextEncompass: string;
};

const styles = require('./VideoEventListBulkScoreModal.scss') as Styles;

const _VideoEventListBulkScoreModal = (props: VideoEventListBulkScoreModalProps) => {
    const {
        isOpen,
        handleModalClosed,
        selectedEventsCount,
        videoEventRatings,
        handleSubmit,
        getSelectedEvents,
        refreshVideoEventsList,
        isSingleScore,
        skipSubmitMessage,
    } = props;
    const [score, setScore] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [disabledSubmit, setDisabledSubmit] = useState(true);

    const [loaderDetail, setLoaderDetail] = useState<loaderDetailInfo>({
        message: `Please wait while all events are being ${score === '0' ? 'cleared' : 'scored'}. \n This could take a few minutes`,
        color: 'black',
        progressLoaderState: progressLoaderEnum.loading,
    });

    const negativeRatings = videoEventRatings.filter((x) => x.value && x.value <= 0);
    const positiveRatings = videoEventRatings.filter((x) => x.value && x.value > 0);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setScore((event.target as HTMLInputElement).value);
    };

    const handleOnSubmit = async () => {
        setLoaderDetail({
            message: `Please wait while all events are being ${score === '0' ? 'cleared' : 'scored'}. \n This could take a few minutes`,
            color: 'black',
            progressLoaderState: progressLoaderEnum.loading,
        });
        setShowLoader(!skipSubmitMessage);
        const videoEventIdList = await getSelectedEvents();
        if (videoEventIdList.length == 0) {
            setLoaderDetail({
                message: `There's been an issue ${score === '0' ? 'clearing' : 'scoring'} the events. \n Please select a shorter date range and try again.`,
                color: scssStyles.styleEnvironment === 'encompass' ? '#F8333C' : '#DE3030',
                progressLoaderState: progressLoaderEnum.error,
            });

            return;
        }
        const callSuccess = await handleSubmit({
            ratingValue: parseInt(score),
            videoEventIdList: videoEventIdList,
        });

        if (skipSubmitMessage) {
            handleOnClose();
        } else if (callSuccess) {
            setLoaderDetail({
                message: `All events have been ${score === '0' ? 'cleared' : 'scored'}.`,
                color: scssStyles.styleEnvironment === 'encompass' ? '#00A878' : '#4CAF50',
                progressLoaderState: progressLoaderEnum.success,
            });

            setTimeout(() => {
                refreshVideoEventsList && refreshVideoEventsList();
                handleOnClose();
            }, 5000);
        } else {
            setLoaderDetail({
                message: `There's been an issue ${score === '0' ? 'clearing' : 'scoring'} the events. \n Please select a shorter date range and try again.`,
                color: scssStyles.styleEnvironment === 'encompass' ? '#F8333C' : '#DE3030',
                progressLoaderState: progressLoaderEnum.error,
            });
        }
    };

    const handleOnClose = () => {
        if (loaderDetail.progressLoaderState === progressLoaderEnum.success && refreshVideoEventsList) {
            refreshVideoEventsList();
        }
        setScore('');
        setDisabledSubmit(true);
        handleModalClosed(isSingleScore);
    };

    const radioOption = (rating: VideoEventScoreResponse, idx: any) => (
        <FormControlLabel
            key={idx}
            value={rating?.value?.toString()}
            control={<StyledRadio color={'primary'} size={'small'} />}
            label={rating.displayName}
            style={{ marginBottom: -10 }}
            onChange={() => setDisabledSubmit(false)}
        />
    );

    const handleClearScore = () => {
        const hasPreviousScore = (props.selectedEvent && props.selectedEvent.score) || !isSingleScore;
        setScore(hasPreviousScore ? '0' : '');
        setDisabledSubmit(!hasPreviousScore);
    };

    const contentMessage = () => (
        <div>
            <FormControl>
                <RadioGroup name="video-event-rating" onChange={handleChange} value={score?.toString()} defaultValue={score}>
                    <Grid container style={{ width: '365px' }}>
                        <Grid item xs={8}>
                            {negativeRatings.map((rating, idx) => (
                                <Grid item key={idx} xs={12}>
                                    {radioOption(rating, idx)}
                                </Grid>
                            ))}
                        </Grid>
                        <Grid item xs={4}>
                            {positiveRatings.map((rating, idx) => (
                                <Grid item key={idx} xs={12}>
                                    {radioOption(rating, idx)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </RadioGroup>
            </FormControl>
            {(score !== '' || !isSingleScore) && (
                <div className={styles.clearScoreSection}>
                    <span
                        className={isEncompass ? styles.clearScoreTextEncompass : styles.clearScoreText}
                        onClick={() => handleClearScore()}
                    >
                        Clear Score
                    </span>
                </div>
            )}
        </div>
    );

    useEffect(() => {
        if (isOpen) setShowLoader(false);
    }, [isOpen]);

    useEffect(() => {
        if (props.selectedEvent && props.selectedEvent.score) {
            setScore(props.selectedEvent.score.toString());
        }
    }, [props.selectedEvent, props.isOpen]);

    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={`${score === '0' ? 'Clear' : 'Score'} ${isSingleScore ? '' : selectedEventsCount} Event${selectedEventsCount > 1 ? 's' : ''}`}
            contentMessage={contentMessage()}
            isLoader={showLoader}
            loaderContainerWidth={'400px'}
            loaderDetail={loaderDetail}
            cancelButton={'Cancel'}
            acceptButton={'Save'}
            handleModalClosed={handleOnClose}
            handleSubmit={handleOnSubmit}
            disabledSubmit={disabledSubmit}
        />
    );
};

export const VideoEventListBulkScoreModal = _VideoEventListBulkScoreModal;
